import { fromSeoUrl, toSeoUrl } from 'helpers/HelperFunctions';
import toSentenceCase from 'helpers/sentenceCase';
import { normalizeJobsRoleSlug } from './getJobsCanonical';
import jobsSeoConfig from './jobsSeoConfig.json';

const TITLE_BRAND = ' | Twine';
const TITLE_AND_REMOTE = 'and remote ';
const TITLE_MAX_LENGTH = 60;

function collapseWhitespace(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function joinParts(parts) {
  return collapseWhitespace(parts.filter(Boolean).join(' '));
}

function formatCount(n) {
  const count = Number(n);
  if (!Number.isFinite(count)) {
    return '0';
  }
  return count.toLocaleString('en-US');
}

function normalizeCountryKey(country) {
  if (!country) {
    return null;
  }
  return toSeoUrl(String(country));
}

function countryPlainName(countryKey) {
  if (!countryKey) {
    return '';
  }
  return fromSeoUrl(countryKey);
}

/**
 * Location phrase used after "in " (e.g. "the UK", "Glasgow, UK", "France").
 * @param {string|null|undefined} country
 * @param {string|null|undefined} city
 * @returns {string}
 */
export function formatJobsLocation(country, city) {
  const countryKey = normalizeCountryKey(country);
  if (!countryKey) {
    return '';
  }

  const shortName =
    jobsSeoConfig.countryShortNames[countryKey] || countryPlainName(countryKey);
  const displayName =
    jobsSeoConfig.countryDisplayNames[countryKey] ||
    countryPlainName(countryKey);

  if (city) {
    const cityName = fromSeoUrl(String(city));
    return joinParts([cityName + ',', shortName]);
  }

  return displayName;
}

/**
 * Search phrase for a role slug (or display name). Uses marketing map first.
 * @param {string|null|undefined} role
 * @param {string|null|undefined} roleDisplayName
 * @returns {string}
 */
export function getRoleSearchName(role, roleDisplayName) {
  const slug = normalizeJobsRoleSlug(role);
  if (slug && jobsSeoConfig.roleSearchNames[slug]) {
    return jobsSeoConfig.roleSearchNames[slug];
  }

  if (roleDisplayName) {
    return toSentenceCase(roleDisplayName);
  }

  if (!role) {
    return '';
  }

  // Slug/name with no map entry: sentence-case the humanized slug.
  return toSentenceCase(fromSeoUrl(String(role).replace(/-/g, ' ')));
}

function getWrittenIntro(role) {
  const slug = normalizeJobsRoleSlug(role);
  if (!slug) {
    return '';
  }
  const intro = jobsSeoConfig.roleIntros[slug];
  return intro && String(intro).trim() ? String(intro).trim() : '';
}

function shortenTitle(baseTitle) {
  let title = collapseWhitespace(`${baseTitle}${TITLE_BRAND}`);
  if (title.length <= TITLE_MAX_LENGTH) {
    return title;
  }

  const withoutRemote = collapseWhitespace(
    baseTitle.replace(TITLE_AND_REMOTE, '')
  );
  title = collapseWhitespace(`${withoutRemote}${TITLE_BRAND}`);
  if (title.length <= TITLE_MAX_LENGTH) {
    return title;
  }

  return withoutRemote;
}

function buildMetaDescription({ roleSearchName, location, liveJobCount }) {
  const browse =
    Number(liveJobCount) < 10
      ? 'Browse the latest'
      : `Browse ${formatCount(liveJobCount)}`;

  if (!roleSearchName && !location) {
    return collapseWhitespace(
      `${browse} freelance and remote jobs in design, video, development, marketing and more. New jobs every day, newest first.`
    );
  }

  if (roleSearchName && !location) {
    return collapseWhitespace(
      `${browse} freelance and remote ${roleSearchName} jobs, newest first. Apply directly to clients on Twine.`
    );
  }

  if (!roleSearchName && location) {
    return collapseWhitespace(
      `${browse} freelance and remote jobs in ${location}, newest first. New jobs every day.`
    );
  }

  return collapseWhitespace(
    `${browse} freelance and remote ${roleSearchName} jobs in ${location}, newest first. New jobs every day.`
  );
}

function buildIntro({
  roleSearchName,
  location,
  role,
  jobsPostedThisWeek,
  isLoggedIn,
}) {
  if (isLoggedIn) {
    return '';
  }

  const written = getWrittenIntro(role);
  let firstSentence = written;
  if (!firstSentence) {
    if (!roleSearchName && !location) {
      firstSentence =
        'Find freelance and remote work in design, video, development, marketing and more.';
    } else {
      firstSentence = joinParts([
        'Find freelance and remote',
        roleSearchName,
        'work',
        location ? `in ${location}` : '',
      ]);
      firstSentence = `${firstSentence}.`;
    }
  }

  const weekCount = Number(jobsPostedThisWeek);
  if (Number.isFinite(weekCount) && weekCount >= 1) {
    return collapseWhitespace(
      `${firstSentence} ${formatCount(weekCount)} jobs posted this week.`
    );
  }

  return collapseWhitespace(firstSentence);
}

/**
 * SEO and page-header copy for every /jobs listing route type.
 *
 * @param {object} options
 * @param {string} [options.role] - role slug or name
 * @param {string} [options.roleDisplayName] - resolved display name for fallbacks
 * @param {string} [options.country]
 * @param {string} [options.city]
 * @param {number} [options.liveJobCount]
 * @param {number} [options.jobsPostedThisWeek] - omit / 0 until an API count exists
 * @param {boolean} [options.isLoggedIn]
 * @returns {{ title: string, metaDescription: string, h1: string, intro: string, resultsLine: string }}
 */
export default function buildJobsListingCopy({
  role = null,
  roleDisplayName = null,
  country = null,
  city = null,
  liveJobCount = 0,
  jobsPostedThisWeek = 0,
  isLoggedIn = false,
} = {}) {
  const roleSearchName = getRoleSearchName(role, roleDisplayName);
  const location = formatJobsLocation(country, city);

  const h1 = joinParts([
    'Freelance and remote',
    roleSearchName,
    'jobs',
    location ? `in ${location}` : '',
  ]);

  const title =
    !roleSearchName && !location
      ? 'Freelance and remote jobs, updated daily | Twine'
      : shortenTitle(h1);

  const resultsCore =
    roleSearchName || location
      ? joinParts([
          'Latest',
          roleSearchName,
          'jobs',
          location ? `in ${location}` : '',
        ])
      : 'Latest jobs';

  return {
    title,
    metaDescription: buildMetaDescription({
      roleSearchName,
      location,
      liveJobCount,
    }),
    h1,
    intro: buildIntro({
      roleSearchName,
      location,
      role,
      jobsPostedThisWeek,
      isLoggedIn,
    }),
    resultsLine: `${resultsCore} from Twine and other job sites · newest first · Try Best match`,
  };
}
